import { useState, useEffect } from 'react';
import ReactDOM from 'react-dom';
import { BrgGroup, NewEventFormData, BrgEvent } from '../types';
import { TimeInput12Hour } from '../../../components/shared/TimeInput12Hour';
import { DateInputDDMMYYYY } from '../../../components/shared/DateInputDDMMYYYY';

interface NewEventModalProps {
  onClose: () => void;
  onSave: (formData: NewEventFormData) => void;
  brgGroups: BrgGroup[];
  initialData?: BrgEvent;
  isEditMode?: boolean;
  currentUserEmail?: string;
  currentUserName?: string;
  isAdmin?: boolean;
}

/**
 * New Event Modal Component
 * Form for creating/editing calendar events
 * Includes validation and submission handling
 * Rendered as a Portal at the document root to avoid positioning context issues
 */
export default function NewEventModal({
  onClose,
  onSave,
  brgGroups,
  initialData,
  isEditMode = false,
}: NewEventModalProps) {
  const [mounted, setMounted] = useState(false);
  
  useEffect(() => {
    setMounted(true);
  }, []);

  const [formData, setFormData] = useState<NewEventFormData>({
    eventName: initialData?.eventName || '',
    brgId: initialData?.brgId || (brgGroups.length === 1 ? brgGroups[0].id : 0),
    date: initialData?.date || '',
    time: initialData?.time || '',
    organizer: initialData?.organizer || '',
    location: initialData?.location || 'Microsoft Teams',
    acceptedAttendees: initialData?.acceptedAttendees || '',
    eventDescription: initialData?.eventDescription || '',
  });

  const [errors, setErrors] = useState<Record<string, string>>({});

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>
  ) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: name === 'brgId' ? parseInt(value) : value,
    }));
    // Clear error for this field
    if (errors[name]) {
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[name];
        return newErrors;
      });
    }
  };

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!formData.eventName.trim()) {
      newErrors.eventName = 'Event name is required';
    }
    if (!formData.brgId) {
      newErrors.brgId = 'BRG is required';
    }
    if (!formData.date) {
      newErrors.date = 'Date is required';
    }
    if (!formData.time) {
      newErrors.time = 'Time is required';
    }
    if (!formData.organizer.trim()) {
      newErrors.organizer = 'Organizer is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const isFormValid = (): boolean => {
    return (
      formData.eventName.trim() !== '' &&
      formData.brgId !== 0 &&
      formData.date !== '' &&
      formData.time !== '' &&
      formData.organizer.trim() !== ''
    );
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (validateForm()) {
      onSave(formData);
    }
  };

  const modalContent = (
    <>
      {/* Overlay */}
      <div
        style={{
          position: 'fixed',
          inset: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          zIndex: 99999,
        }}
        onClick={onClose}
      />

      {/* Modal */}
      <div
        style={{
          position: 'fixed',
          top: '40px',
          left: '50%',
          transform: 'translateX(-50%)',
          zIndex: 100000,
          backgroundColor: 'white',
          borderRadius: '8px',
          boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1)',
          width: '480px',
          maxWidth: '95vw',
          maxHeight: '90vh',
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        {/* Header */}
        <div
          style={{
            backgroundColor: '#1f40af',
            color: 'white',
            padding: '16px 24px',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            borderRadius: '8px 8px 0 0',
            flexShrink: 0,
          }}
        >
          <h2 style={{ fontSize: '16px', fontWeight: 'bold', margin: 0 }}>
            {isEditMode ? '✏️ Edit Event' : '✚ Add New Event'}
          </h2>
          <button
            onClick={onClose}
            style={{
              backgroundColor: 'transparent',
              color: 'white',
              border: 'none',
              fontSize: '24px',
              fontWeight: 'bold',
              cursor: 'pointer',
              padding: 0,
              lineHeight: 1,
            }}
          >
            ×
          </button>
        </div>

        {/* Form Container - Scrollable */}
        <form onSubmit={handleSubmit} style={{ padding: '24px', overflowY: 'auto', flex: 1 }}>
          {/* Event Name */}
          <div style={{ marginBottom: '16px' }}>
            <label style={{ fontSize: '12px', fontWeight: 'bold', color: '#374151', display: 'block', marginBottom: '6px' }}>
              Event Name <span style={{ color: '#ef4444' }}>*</span>
            </label>
            <input
              type="text"
              name="eventName"
              placeholder="Enter event name"
              value={formData.eventName}
              onChange={handleChange}
              style={{
                width: '100%',
                border: errors.eventName ? '1px solid #ef4444' : '1px solid #d1d5db',
                borderRadius: '4px',
                padding: '8px 12px',
                fontSize: '14px',
                boxSizing: 'border-box',
                outline: 'none',
              }}
              onFocus={(e) => {
                e.target.style.boxShadow = '0 0 0 3px rgba(31, 64, 175, 0.1)';
              }}
              onBlur={(e) => {
                e.target.style.boxShadow = 'none';
              }}
            />
            {errors.eventName && (
              <p style={{ fontSize: '12px', color: '#ef4444', marginTop: '4px', margin: 0 }}>
                {errors.eventName}
              </p>
            )}
          </div>

          {/* BRG */}
          <div style={{ marginBottom: '16px' }}>
            <label style={{ fontSize: '12px', fontWeight: 'bold', color: '#374151', display: 'block', marginBottom: '6px' }}>
              BRG <span style={{ color: '#ef4444' }}>*</span>
              {brgGroups.length === 1 && (
                <span style={{ fontSize: '11px', color: '#6b7280', fontWeight: 'normal', marginLeft: '6px' }}>
                  (Your assigned BRG)
                </span>
              )}
            </label>
            <select
              name="brgId"
              value={formData.brgId || ''}
              onChange={handleChange}
              disabled={isEditMode || brgGroups.length === 1}
              style={{
                width: '100%',
                border: errors.brgId ? '1px solid #ef4444' : '1px solid #d1d5db',
                borderRadius: '4px',
                padding: '8px 12px',
                fontSize: '14px',
                backgroundColor: (isEditMode || brgGroups.length === 1) ? '#f3f4f6' : 'white',
                boxSizing: 'border-box',
                outline: 'none',
                cursor: (isEditMode || brgGroups.length === 1) ? 'not-allowed' : 'pointer',
                color: (isEditMode || brgGroups.length === 1) ? '#6b7280' : '#111827',
              }}
              onFocus={(e) => {
                e.target.style.boxShadow = '0 0 0 3px rgba(31, 64, 175, 0.1)';
              }}
              onBlur={(e) => {
                e.target.style.boxShadow = 'none';
              }}
            >
              <option value="">Select BRG</option>
              {brgGroups.map(brg => (
                <option key={brg.id} value={brg.id}>
                  {brg.name}
                </option>
              ))}
            </select>
            {errors.brgId && (
              <p style={{ fontSize: '12px', color: '#ef4444', marginTop: '4px', margin: 0 }}>
                {errors.brgId}
              </p>
            )}
          </div>

          {/* Date and Time (inline) */}
          <div style={{ marginBottom: '16px', display: 'flex', gap: '12px' }}>
            <div style={{ flex: 1 }}>
              <label style={{ fontSize: '12px', fontWeight: 'bold', color: '#374151', display: 'block', marginBottom: '6px' }}>
                Date <span style={{ color: '#ef4444' }}>*</span>
              </label>
              <DateInputDDMMYYYY
                value={formData.date}
                onChange={(val) => handleChange({ target: { name: 'date', value: val } } as React.ChangeEvent<HTMLInputElement>)}
                style={{
                  width: '100%',
                  border: errors.date ? '1px solid #ef4444' : '1px solid #d1d5db',
                  borderRadius: '4px',
                  padding: '8px 12px',
                  fontSize: '14px',
                  boxSizing: 'border-box',
                  outline: 'none',
                }}
              />
              {errors.date && (
                <p style={{ fontSize: '12px', color: '#ef4444', marginTop: '4px', margin: 0 }}>
                  {errors.date}
                </p>
              )}
            </div>

            <div style={{ flex: 1 }}>
              <label style={{ fontSize: '12px', fontWeight: 'bold', color: '#374151', display: 'block', marginBottom: '6px' }}>
                Time <span style={{ color: '#ef4444' }}>*</span>
              </label>
              <TimeInput12Hour
                value={formData.time}
                onChange={(val) => handleChange({ target: { name: 'time', value: val } } as React.ChangeEvent<HTMLInputElement>)}
                required
              />
              {errors.time && (
                <p style={{ fontSize: '12px', color: '#ef4444', marginTop: '4px', margin: 0 }}>
                  {errors.time}
                </p>
              )}
            </div>
          </div>

          {/* Organizer */}
          <div style={{ marginBottom: '16px' }}>
            <label style={{ fontSize: '12px', fontWeight: 'bold', color: '#374151', display: 'block', marginBottom: '6px' }}>
              Organizer <span style={{ color: '#ef4444' }}>*</span>
            </label>
            <input
              type="text"
              name="organizer"
              placeholder="Enter organizer name"
              value={formData.organizer}
              onChange={handleChange}
              style={{
                width: '100%',
                border: errors.organizer ? '1px solid #ef4444' : '1px solid #d1d5db',
                borderRadius: '4px',
                padding: '8px 12px',
                fontSize: '14px',
                boxSizing: 'border-box',
                outline: 'none',
              }}
              onFocus={(e) => {
                e.target.style.boxShadow = '0 0 0 3px rgba(31, 64, 175, 0.1)';
              }}
              onBlur={(e) => {
                e.target.style.boxShadow = 'none';
              }}
            />
            {errors.organizer && (
              <p style={{ fontSize: '12px', color: '#ef4444', marginTop: '4px', margin: 0 }}>
                {errors.organizer}
              </p>
            )}
          </div>

          {/* Location */}
          <div style={{ marginBottom: '16px' }}>
            <label style={{ fontSize: '12px', fontWeight: 'bold', color: '#374151', display: 'block', marginBottom: '6px' }}>
              Location
            </label>
            <input
              type="text"
              name="location"
              value={formData.location}
              disabled
              style={{
                width: '100%',
                border: '1px solid #d1d5db',
                borderRadius: '4px',
                padding: '8px 12px',
                fontSize: '14px',
                boxSizing: 'border-box',
                outline: 'none',
                backgroundColor: '#f3f4f6',
                color: '#6b7280',
                cursor: 'not-allowed',
              }}
              onFocus={(e) => {
                e.target.style.boxShadow = 'none';
              }}
              onBlur={(e) => {
                e.target.style.boxShadow = 'none';
              }}
            />
          </div>

          {/* Accepted Attendees */}
          <div style={{ marginBottom: '16px' }}>
            <label style={{ fontSize: '12px', fontWeight: 'bold', color: '#374151', display: 'block', marginBottom: '6px' }}>
              Accepted Attendees
            </label>
            <input
              type="text"
              name="acceptedAttendees"
              placeholder="e.g. 18 members"
              value={formData.acceptedAttendees}
              onChange={handleChange}
              style={{
                width: '100%',
                border: '1px solid #d1d5db',
                borderRadius: '4px',
                padding: '8px 12px',
                fontSize: '14px',
                boxSizing: 'border-box',
                outline: 'none',
              }}
              onFocus={(e) => {
                e.target.style.boxShadow = '0 0 0 3px rgba(31, 64, 175, 0.1)';
              }}
              onBlur={(e) => {
                e.target.style.boxShadow = 'none';
              }}
            />
          </div>

          {/* Event Description */}
          <div style={{ marginBottom: '24px' }}>
            <label style={{ fontSize: '12px', fontWeight: 'bold', color: '#374151', display: 'block', marginBottom: '6px' }}>
              Event Description
            </label>
            <textarea
              name="eventDescription"
              placeholder="Describe the event..."
              value={formData.eventDescription}
              onChange={handleChange}
              rows={4}
              style={{
                width: '100%',
                border: '1px solid #d1d5db',
                borderRadius: '4px',
                padding: '8px 12px',
                fontSize: '14px',
                boxSizing: 'border-box',
                outline: 'none',
                fontFamily: 'inherit',
                resize: 'vertical',
              }}
              onFocus={(e) => {
                e.target.style.boxShadow = '0 0 0 3px rgba(31, 64, 175, 0.1)';
              }}
              onBlur={(e) => {
                e.target.style.boxShadow = 'none';
              }}
            />
          </div>
        </form>

        {/* Actions Footer - Fixed at bottom */}
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '12px', padding: '16px 24px', borderTop: '1px solid #e5e7eb', flexShrink: 0, backgroundColor: 'white' }}>
            <button
              type="button"
              onClick={onClose}
              style={{
                padding: '8px 16px',
                backgroundColor: 'white',
                color: '#374151',
                border: '1px solid #d1d5db',
                borderRadius: '4px',
                fontSize: '14px',
                fontWeight: 'bold',
                cursor: 'pointer',
                transition: 'background-color 0.2s',
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = '#f3f4f6';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = 'white';
              }}
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={!isFormValid()}
              onClick={(e) => {
                e.preventDefault();
                const form = (e.target as HTMLButtonElement).closest('form') || document.querySelector('form');
                if (form) {
                  const event = new Event('submit', { bubbles: true, cancelable: true });
                  form.dispatchEvent(event);
                }
              }}
              style={{
                padding: '8px 20px',
                backgroundColor: isFormValid() ? '#1f40af' : '#cbd5e1',
                color: isFormValid() ? 'white' : '#94a3b8',
                border: 'none',
                borderRadius: '4px',
                fontSize: '14px',
                fontWeight: 'bold',
                cursor: isFormValid() ? 'pointer' : 'not-allowed',
                transition: 'background-color 0.2s',
              }}
              onMouseEnter={(e) => {
                if (isFormValid()) {
                  e.currentTarget.style.backgroundColor = '#1a329d';
                }
              }}
              onMouseLeave={(e) => {
                if (isFormValid()) {
                  e.currentTarget.style.backgroundColor = '#1f40af';
                }
              }}
            >
              {isEditMode ? 'Update Event' : 'Save Event'}
            </button>
          </div>
      </div>
    </>
  );

  if (!mounted) return null;

  return ReactDOM.createPortal(
    modalContent,
    document.body
  );
}
